import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { KnowledgeSource } from '@vendasta/embeddings';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyDataSource } from '@vendasta/galaxy/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { KnowledgeBaseTableApi } from './knowledge-base-table.api';
import { KnowledgeSourceTableApi } from './knowledge-source-table.api';

import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { AiKnowledgeService } from '../services/ai-knowledge.service';
import {
  CAN_SHARE_TOKEN,
  EDITING_ENABLED_TOKEN,
  KNOWLEDGE_GRANTS_ENABLED_TOKEN,
  SHOW_BUSINESS_PROFILE_SOURCE_TOKEN,
} from '../tokens';
import { KnowledgeBaseTableComponent } from './knowledge-base-table/knowledge-base-table.component';
import { KnowledgeRowMapper } from './knowledge-row.mapper';
import { KnowledgeSourceTableComponent } from './knowledge-source-table/knowledge-source-table.component';
import {
  TableKnowledgeBase,
  TableKnowledgeSource,
} from './types';

export interface KnowledgePageContext {
  /** i18n key for the page title, rendered through the `translate` pipe (e.g. `'KNOWLEDGE'`). */
  pageTitle: string;
  /** Override for the derived CAN_SHARE_TOKEN (storybook). */
  canShare?: boolean;
  /** i18n key for the sources tab label. Defaults to `'KNOWLEDGE_SHARING.TABS.SOURCES'`. */
  sourcesTabLabel?: string;
  /** i18n key for the bases tab label. Defaults to `'KNOWLEDGE_SHARING.TABS.COLLECTIONS'`. */
  basesTabLabel?: string;
}

/**
 * Knowledge management page. A thin container that fetches knowledge bases + sources, derives each
 * row's `managedBy` from its namespace, and hands the rows to the sources / bases table views. The
 * tab switcher uses mat-button-toggle + @if rather than mat-tab-group, whose absolutely-positioned
 * body reports zero height inside glxy-page and breaks scrolling.
 *
 * Pass `kbRows` / `ksRows` to render canned data; omit them to fetch from `AiKnowledgeService`.
 */
@Component({
  selector: 'ai-knowledge-knowledge-management',
  imports: [
    CommonModule,
    GalaxyPageModule,
    MatTabsModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    KnowledgeSourceTableComponent,
    KnowledgeBaseTableComponent,
  ],
  templateUrl: './knowledge-management.component.html',
  styleUrls: ['./knowledge-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [KnowledgeRowMapper],
})
export class KnowledgeManagementComponent implements OnInit {
  @Input({ required: true }) context!: KnowledgePageContext;
  /** When set, skips the service fetch (storybook/tests). */
  @Input() set kbRows(value: TableKnowledgeBase[] | undefined) {
    if (value !== undefined) {
      this.kbRowsData.set(value);
      this.skipServiceFetch.set(true);
    }
  }
  /** When set, skips the service fetch (storybook/tests). */
  @Input() set ksRows(value: TableKnowledgeSource[] | undefined) {
    if (value !== undefined) {
      this.ksRowsData.set(value);
      this.skipServiceFetch.set(true);
    }
  }
  /** Seed the raw `KnowledgeSource` configs (storybook/tests) so the sources table's edit/refresh/delete are live. */
  @Input() set rawSources(value: Map<string, KnowledgeSource> | undefined) {
    if (value !== undefined) {
      this.rawSourcesById.set(value);
    }
  }
  /** Render Share as a top-level row action on the bases table instead of inside the overflow menu. */
  @Input() shareAsTopLevelAction = false;
  /** When true, clicking a KB name emits `openKbDetail` (navigate to the dedicated page) instead of opening the sources dialog. */
  @Input() openDetailOnRowClick = false;
  /** Bubbled up from the bases table when a row asks to open the dedicated KB detail page. */
  @Output() openKbDetail = new EventEmitter<TableKnowledgeBase>();

  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly mapper = inject(KnowledgeRowMapper);
  private readonly canShare$ = inject(CAN_SHARE_TOKEN);
  private readonly editingEnabled$ = inject(EDITING_ENABLED_TOKEN);
  private readonly grantsEnabled$ = inject(KNOWLEDGE_GRANTS_ENABLED_TOKEN);
  private readonly showBusinessProfileSource$ = inject(SHOW_BUSINESS_PROFILE_SOURCE_TOKEN);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** URL query-param key the active tab is encoded under, so the selection is deep-linkable and survives navigating into and back out of a KB. */
  private static readonly TAB_PARAM = 'tab';

  readonly kbRowsData = signal<TableKnowledgeBase[]>([]);
  readonly ksRowsData = signal<TableKnowledgeSource[]>([]);
  /** Raw sources by ID, so the sources table's Edit/Refresh/Delete have the full config. Empty in @Input mode. */
  readonly rawSourcesById = signal<Map<string, KnowledgeSource>>(new Map());
  readonly activeTab = signal<'sources' | 'bases'>('sources');
  /** Gates the auto-fetch when rows are passed in. */
  private readonly skipServiceFetch = signal(false);

  // Latest token values, as signals for the template. All default to the least-privileged value so a
  // pending/failed token resolution never briefly exposes Share or edit affordances.
  readonly canShareToken = signal(false);
  readonly editingEnabled = signal(false);
  readonly grantsEnabledToken = signal(false);

  /** Share UI requires the grants flag AND canShare; the storybook `context.canShare` override bypasses the flag. */
  readonly canShareEffective = computed(() => {
    if (this.context?.canShare !== undefined) return this.context.canShare;
    return this.grantsEnabledToken() && this.canShareToken();
  });

  /** Exposed for the base table's `persist` input (false in storybook/@Input mode). */
  readonly persistChanges = computed(() => !this.skipServiceFetch());

  /**
   * Each table gets ONE stable `GalaxyDataSource`, created once (the galaxy table binds its data source only
   * at init and ignores later input swaps — every other consumer keeps a stable instance too).
   *
   * Knowledge bases are server-paged: the table's page size / cursor / search drive the query via
   * `KnowledgeBaseTableApi` (no fetch-all). Storybook / @Input mode passes canned rows, which the API serves
   * in-memory. Sources are still client-paged over a live-reading `StaticTableApi` (server pagination for
   * sources is a follow-up); when their rows change we call `refreshDataSource` to re-pull.
   */
  readonly kbDataSource = new GalaxyDataSource<TableKnowledgeBase, unknown, unknown>(
    new KnowledgeBaseTableApi(
      this.knowledgeService,
      this.mapper,
      () => (this.skipServiceFetch() ? this.kbRowsData() : null),
      this.grantsEnabled$,
    ),
  );
  readonly ksDataSource = new GalaxyDataSource<TableKnowledgeSource, unknown, unknown>(
    new KnowledgeSourceTableApi(
      this.knowledgeService,
      this.mapper,
      () => (this.skipServiceFetch() ? this.ksRowsData() : null),
      // Keep the source table's per-page inputs in sync with the loaded page.
      (page) => {
        this.ksRowsData.set(page.rows);
        this.rawSourcesById.set(page.rawById);
      },
      () => this.resolveFirstPageBusinessProfile(),
      this.grantsEnabled$,
    ),
  );

  /**
   * Lazily fetch the full owned-source candidate list for the KB create / manage-sources dialogs — invoked
   * only when a dialog opens (server pagination for the picker itself is a separate follow-up; those dialogs
   * still need the whole candidate set). Arrow field so `this` binds when passed as the table's loader.
   */
  readonly fetchOwnedSourceRows = async (): Promise<TableKnowledgeSource[]> => {
    const callerNamespace = this.knowledgeService.buildNamespaceInterface();
    const sources = await this.knowledgeService.listAllKnowledgeSources();
    return Promise.all(sources.map((ks) => this.mapper.toKsRow(ks, callerNamespace)));
  };

  /**
   * Force a stable `GalaxyDataSource` to re-pull from its (live-reading) `StaticTableApi` after the backing
   * rows change. Re-issuing the current search term is the data source's public refetch trigger; it lands
   * back on page 1, which is the right behavior after a data load / create / delete.
   */
  private refreshDataSource(ds: GalaxyDataSource<unknown, unknown, unknown>): void {
    ds.setSearchTerm(ds.state.searchTerm ?? '');
  }

  /** A KB was created in the table — prepend it and refresh the bases data source. */
  onKbCreated(kb: TableKnowledgeBase): void {
    this.kbRowsData.update((list) => [kb, ...list]);
    this.refreshDataSource(this.kbDataSource);
  }

  /** A KB's source membership changed — update that row in place and refresh. */
  onKbSourcesChanged(change: { kbKey: string; sourceIds: string[] }): void {
    this.kbRowsData.update((list) =>
      list.map((row) =>
        row.kbKey === change.kbKey
          ? { ...row, sourceIds: [...change.sourceIds], sourceCount: change.sourceIds.length, lastModified: new Date() }
          : row,
      ),
    );
    this.refreshDataSource(this.kbDataSource);
  }

  /** A KB was deleted — drop it from the row set and refresh. */
  onKbDeleted(kb: TableKnowledgeBase): void {
    this.kbRowsData.update((list) => list.filter((row) => row.kbKey !== kb.kbKey));
    this.refreshDataSource(this.kbDataSource);
  }

  async ngOnInit(): Promise<void> {
    // Restore the active tab from the URL so the selection is deep-linkable and survives a round-trip
    // through a KB detail page (the param persists while the detail is open, so Back lands on the same tab).
    const tabParam = this.route.snapshot.queryParamMap.get(KnowledgeManagementComponent.TAB_PARAM);
    this.activeTab.set(tabParam === 'collections' ? 'bases' : 'sources');

    this.canShareToken.set((await firstValueFrom(this.canShare$).catch(() => false)) ?? false);
    this.editingEnabled.set((await firstValueFrom(this.editingEnabled$).catch(() => false)) ?? false);
    this.grantsEnabledToken.set((await firstValueFrom(this.grantsEnabled$).catch(() => false)) ?? false);
    // No explicit data load: both tables are server-paged and fetch on connect; the KB dialogs' candidate
    // list loads lazily when a dialog opens (see fetchOwnedSourceRows).
  }

  /** Switch tabs and encode the selection in the URL (`?tab=sources|collections`) without adding a history entry. */
  onTabChange(value: 'sources' | 'bases'): void {
    this.activeTab.set(value);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [KnowledgeManagementComponent.TAB_PARAM]: value === 'bases' ? 'collections' : 'sources' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** A source-table action mutated server state — re-pull the current sources page. No-op in canned mode. */
  onSourcesChanged(): void {
    if (this.skipServiceFetch()) return;
    this.refreshDataSource(this.ksDataSource);
  }

  /**
   * The synthetic Business Profile source to surface on the first sources page when the BE hasn't
   * materialized it yet (Business Center only — `showBusinessProfileSource` is false elsewhere). The source
   * table API decides whether to prepend it based on the page's contents.
   */
  private async resolveFirstPageBusinessProfile(): Promise<KnowledgeSource | null> {
    const showBP = (await firstValueFrom(this.showBusinessProfileSource$).catch(() => false)) ?? false;
    if (!showBP) return null;
    return this.knowledgeService.buildBusinessProfileKnowledgeSource(true);
  }
}
